/**
 * Component for adding or editing client information within a dialog.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Determines if the dialog is open.
 * @param {() => void} props.onClose - Function to call when the dialog is closed.
 * @param {boolean} [props.isPending] - Optional flag indicating if the operation is pending.
 * @param {string} props.userId - The ID of the user being edited.
 * @param {string} [props.firstName] - The first name of the user.
 * @param {string} [props.lastName] - The last name of the user.
 * @param {string} [props.email] - The email of the user.
 * @param {number} [props.birthYear] - The birth year of the user.
 * @param {Enum_Gender_Enum} [props.gender] - The gender of the user.
 * @param {Enum_Racial_Ethnic_Enum} [props.racialEthnic] - The racial/ethnic background of the user.
 * @param {string} [props.presentingProblem] - The presenting problem of the user.
 *
 * @returns {JSX.Element} The rendered AddEditClientDialog component.
 *
 * @example
 * <AddEditClientDialog
 *   open={true}
 *   onClose={() => {}}
 *   userId="123"
 *   firstName="John"
 *   lastName="Doe"
 *   email="john.doe@example.com"
 *   birthYear={1990}
 *   gender={Enum_Gender_Enum.Male}
 *   racialEthnic={Enum_Racial_Ethnic_Enum.Asian}
 *   presentingProblem="Anxiety"
 * />
 */

import {
  Dialog,
  makeStyles,
  Snackbar,
  SnackbarCloseReason,
} from "@material-ui/core"
import { Alert, Color } from "@material-ui/lab"
import { FC, SyntheticEvent, useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  Enum_Gender_Enum,
  Enum_Racial_Ethnic_Enum,
  useInsertClientDemographicsMutation,
  useUpdateClientDemographicsMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { Query } from "../../utils/constants"
import { colors } from "../../utils/theme"
import AddEditClient from "../molecules/AddEditClient"
import Success from "../molecules/Success"

interface AddEditClientDialogProps {
  open: boolean
  onClose: () => void
  isPending?: boolean
  demographicsDataId: string | undefined
  userId: string
  firstName: string
  lastName: string
  email: string
  birthYear?: number
  gender?: Enum_Gender_Enum
  racialEthnic?: Enum_Racial_Ethnic_Enum
  presentingProblem?: string
}

const AddEditClientDialog: FC<AddEditClientDialogProps> = (props) => {
  const classes = useStyles()
  const {
    firstName,
    lastName,
    racialEthnic,
    birthYear,
    userId,
    presentingProblem,
    gender,
    demographicsDataId,
  } = props
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [clientFirstName, setClientFirstName] = useState<string>(
    firstName ?? ""
  )
  const [clientLastName, setClientLastName] = useState<string>(lastName ?? "")
  const [clientBirthYear, setClientBirthYear] = useState<number | undefined>(
    birthYear ?? undefined
  )
  const [clientGender, setClientGender] = useState<
    Enum_Gender_Enum | undefined
  >(gender ?? undefined)
  const [clientRacialEthnic, setClientRacialEthnic] = useState<
    Enum_Racial_Ethnic_Enum | undefined
  >(racialEthnic)
  const [clientPresentingProblem, setClientPresentingProblem] =
    useState<string>(presentingProblem ?? "")

  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()

  const onSuccess = (data: any) => {
    if (
      data?.insert_client_demographics_one === null ||
      data?.update_client_demographics?.returning?.length === 0
    ) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
      setSubmitted(false)
      return
    }
    queryClient.invalidateQueries({ queryKey: [Query.GET_CLIENT_DATA] })
    setSubmitted(true)
  }
  const { mutateAsync: insertClientDemographics } =
    useInsertClientDemographicsMutation(graphqlClient, {
      onSuccess,
    })
  const { mutateAsync: updateClientDemographics } =
    useUpdateClientDemographicsMutation(graphqlClient, {
      onSuccess,
    })
  useEffect(() => {
    if (props.open) {
      setClientFirstName(firstName ?? "")
      setClientLastName(lastName ?? "")
      setClientBirthYear(birthYear ?? undefined)
      setClientRacialEthnic(racialEthnic ?? undefined)
      setClientGender(gender ?? undefined)
      setClientPresentingProblem(presentingProblem ?? "")
    }
  }, [
    props.open,
    firstName,
    lastName,
    birthYear,
    racialEthnic,
    gender,
    presentingProblem,
  ])

  const saveData = () => {
    const trimmedClientFirstName = clientFirstName.trim() || null
    const trimmedClientLastName = clientLastName.trim() || null
    const trimmedCurrentFirstName = (firstName ?? "").trim()
    const trimmedCurrentLastName = (lastName ?? "").trim()
    const trimmedCurrentPresentingProblem = (presentingProblem ?? "").trim()
    const trimmedClientPresentingProblem = clientPresentingProblem.trim()

    if (
      trimmedCurrentFirstName !== trimmedClientFirstName ||
      trimmedCurrentLastName !== trimmedClientLastName ||
      birthYear !== clientBirthYear ||
      gender !== clientGender ||
      racialEthnic !== clientRacialEthnic ||
      trimmedCurrentPresentingProblem !== trimmedClientPresentingProblem
    ) {
      // Update the existing client demographics data if it exists, otherwise insert new data
      if (demographicsDataId) {
        updateClientDemographics(
          {
            id: demographicsDataId,
            firstName: trimmedClientFirstName,
            lastName: trimmedClientLastName,
            birthYear: clientBirthYear,
            gender: clientGender,
            racialEthnic: clientRacialEthnic,
            description: trimmedClientPresentingProblem,
          },
          {
            onError: () => {
              console.error("Error updating client demographics")
            },
          }
        )
      } else {
        insertClientDemographics(
          {
            userId: userId,
            firstName: trimmedClientFirstName,
            lastName: trimmedClientLastName,
            birthYear: clientBirthYear,
            gender: clientGender,
            racialEthnic: clientRacialEthnic,
            description: trimmedClientPresentingProblem,
          },
          {
            onError: () => {
              console.error("Error inserting client demographics")
            },
          }
        )
      }
    }
  }

  const onEditClose = () => {
    setClientFirstName(firstName ?? "")
    setClientLastName(lastName ?? "")
    setClientBirthYear(birthYear ?? undefined)
    setClientGender(gender ?? undefined)
    setClientRacialEthnic(racialEthnic ?? undefined)
    setClientPresentingProblem(presentingProblem ?? "")
    props.onClose()
  }

  const onSuccessClose = async () => {
    onEditClose()
    await new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
      setSubmitted(false)
    })
  }

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const handleCloseSnackbar = (
    event: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth={true}
      maxWidth="xs"
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      {!submitted ? (
        <AddEditClient
          onClose={onEditClose}
          onSaveData={saveData}
          firstName={clientFirstName}
          lastName={clientLastName}
          email={props.email}
          birthYear={clientBirthYear}
          setBirthYear={setClientBirthYear}
          gender={clientGender}
          setGender={setClientGender}
          racialEthnic={clientRacialEthnic}
          setRacialEthnic={setClientRacialEthnic}
          presentingProblem={clientPresentingProblem}
          setPresentingProblem={setClientPresentingProblem}
          setFirstName={setClientFirstName}
          setLastName={setClientLastName}
        />
      ) : (
        <Success
          onClose={onSuccessClose}
          title="Client Data Saved"
          message={`${props.firstName} ${props.lastName}'s data has been saved successfully`}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  )
}

const useStyles = makeStyles(() => ({
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
    borderRadius: 12,
  },
}))

export default AddEditClientDialog
